import { FormEvent, useEffect, useState } from 'react';
import Alert from '@v2/shared/components/Alert';
import Breadcrumbs from '@v2/shared/components/Breadcrumbs';
import Button from '@v2/shared/components/Button';
import Spinner from '@v2/shared/components/Spinner';
import { FormCheckbox, FormInput, FormInputPhone } from '@v2/shared/components/Form';
import PreferencesTabs from '@v2/shared/components/PreferencesTabs';
import { useSite } from '@v2/shared/lib/site';
import useUrlGenerator from '@v2/shared/lib/useUrlGenerator';
import { useSaveSmsPreferences, useSmsPreferences } from './useSmsPreferences';
import { Props, SmsList } from './types';

/** The account dashboard, still legacy customer.php — the same entry the menu opens with. */
const ACCOUNT_URL = '/customer?xCmd=account';

/**
 * What each list is called on screen. Read by the checkbox and by the messages
 * that name a list which refused a save or is waiting to be confirmed, so they
 * cannot drift apart.
 *
 * The reminders list is named for what it is rather than for the one message we
 * are building first: order notifications — a shipping text, say — go out on the
 * same consent, and a customer who ticked "subscription reminders" would be
 * getting something they had not agreed to. The charge reminder is the
 * description's job.
 */
const LIST_LABELS: Record<SmsList, string> = {
  marketing: 'Deals and offers',
  transactional: 'Order and subscription updates',
};

/** The lists a message is about, named the way the customer sees them. */
function listNames(lists: SmsList[], conjunction: string): string {
  return lists.map((list) => LIST_LABELS[list]).join(` ${conjunction} `);
}

/**
 * /my-account/sms — the customer's phone number, and what we may text it about.
 *
 * The number is the page, not the texting. It is editable here because it is no
 * longer editable anywhere else — the phone number left the address book, and
 * this is the page that owns it now — and it is worth having whether or not we
 * ever text it, since it is also the number somebody rings. So the lists sit
 * under a heading of their own beneath it, and the tab is called Phone.
 *
 * Otherwise the mirror of the email preferences page: a contact detail, a
 * checkbox per list, and a way to leave all of them at once.
 *
 * The two lists are genuinely separate systems (marketing is Klaviyo's,
 * transactional is ours) and the api reports each independently, so the form
 * always redraws from what the server answers rather than from what was
 * submitted. If one list refuses, the customer sees that immediately instead of
 * at the next page load.
 *
 * A list can also answer neither: Klaviyo's is double opt-in, so it takes the
 * subscribe and texts the customer, and the number joins when they reply. That
 * comes back as pending — the box stays ticked and the message says to expect
 * the text. Loading the page again asks Klaviyo itself, which reports the
 * number as not subscribed until they have answered, and the box is empty until
 * then. Both are true; they are answers to different questions.
 *
 * A list is drawn only where the storefront actually holds one, which is why
 * the two checkboxes are asked about separately: they are separate programmes,
 * and a storefront can have either, both or neither. On one with neither — the
 * Canadian and Australian sites — the page is the phone number and nothing
 * else. The api refuses a list it does not hold as well; this is not the gate.
 *
 * The number itself is collected in whichever shape the storefront collects,
 * mirroring what checkout does: USPhoneNumberInput drops its US mask for any
 * country but the US, because international numbers are not a shape you can
 * usefully enforce, and an Australian customer must be able to save the only
 * number they have.
 */
export default function SmsPreferences({
  emailPreferencesUrl,
  smsPreferencesUrl,
  loginUrl,
  usNumbersOnly,
  offersMarketing,
  offersTransactional,
}: Props) {
  const { preferences, setPreferences, isLoading, hasFailed } = useSmsPreferences(loginUrl);

  // The brand the texts come from and the two documents that describe them, both
  // off the storefront being rendered rather than written down — see the consent
  // block below.
  const { name: siteName } = useSite();
  const { buildUrl } = useUrlGenerator();

  const [telephone, setTelephone] = useState('');
  const [marketing, setMarketing] = useState(false);
  const [transactional, setTransactional] = useState(false);

  const { save, unsubscribeAll, isSaving, error, saved } = useSaveSmsPreferences({
    loginUrl,
    onSuccess: setPreferences,
  });

  // The form follows the server, both on load and after every save.
  useEffect(() => {
    if (!preferences) {
      return;
    }

    // On the US storefronts the stored number is E.164 and the masked input
    // wants bare digits, which it renders back as (212) 555-1234. Everywhere
    // else the number is whatever the customer typed and there is nothing to
    // strip — running that through the same digits-only slice would quietly
    // amputate an Australian number down to its last ten characters.
    setTelephone(
      usNumbersOnly ? (preferences.telephone ?? '').replace(/\D/g, '').slice(-10) : preferences.telephone ?? ''
    );
    setMarketing(preferences.marketing);
    setTransactional(preferences.transactional);
  }, [preferences, usNumbersOnly]);

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();

    // Only the lists this storefront actually holds are submitted — the api
    // ignores the rest, and sending consent a form never asked for is the kind
    // of thing that becomes true again when a gate moves.
    save({
      telephone,
      ...(offersMarketing ? { marketing } : {}),
      ...(offersTransactional ? { transactional } : {}),
    });
  };

  const hasNumber = Boolean(preferences?.telephone);
  const isSubscribedToAnything = Boolean(preferences?.marketing || preferences?.transactional);

  // The lists that refused the save. Saying "successfully updated" over the top
  // of a box that has just come back unticked is what made the page look like it
  // was losing saves.
  const failed = preferences?.failed ?? [];

  // What to say instead of "please try again" where the api knows why a list said
  // no. One refusal has a cause the customer could not possibly guess and cannot
  // fix by retrying — a number AWS will not reinstate for 30 days — and that is
  // the one it explains.
  const reasons = failed.map((list) => preferences?.reasons?.[list]).filter(Boolean);

  // And the ones that took it and are waiting on the customer. Klaviyo's list is
  // double opt-in: it texts the number and adds it to the list when they reply,
  // so there is nothing wrong here and something for them to do. It used to be
  // reported as a refusal, which is the save that worked and said it had not.
  const pending = preferences?.pending ?? [];

  return (
    <div>
      {/* This page's trail, not the layout's — see SideMenu. */}
      <Breadcrumbs items={[{ label: 'Account', href: ACCOUNT_URL }, { label: 'Notifications' }]} />

      <h1 className="font-bold text-2xl md:text-3xl mb-3">Notifications</h1>

      <p className="text-sm md:text-base text-slate-600 mb-5 md:mb-8 font-medium max-w-3xl">
        Choose how you would like to hear from us.
      </p>

      <PreferencesTabs
        current="sms"
        emailPreferencesUrl={emailPreferencesUrl}
        smsPreferencesUrl={smsPreferencesUrl}
      />

      {isLoading && (
        <div className="w-full flex items-center justify-center py-10">
          <Spinner className="text-primary text-2xl" />
        </div>
      )}

      {!isLoading && hasFailed && (
        <Alert color="danger">Oops, something went wrong. Try again later.</Alert>
      )}

      {!isLoading && !hasFailed && (
        <form onSubmit={onSubmit} className="max-w-2xl">
          {saved && failed.length === 0 && (
            <Alert color="success" className="mb-5">
              Phone preferences successfully updated
              {pending.length > 0 && (
                <>
                  . We just sent you a text message to confirm your {listNames(pending, 'and')}{' '}
                  subscription — reply to it and you are on the list
                </>
              )}
            </Alert>
          )}

          {failed.length > 0 && (
            <Alert color="danger" className="mb-5">
              We could not update {listNames(failed, 'or')}.{' '}
              {reasons.length > 0 ? reasons.join(' ') : 'Please try again.'}
            </Alert>
          )}

          {error && (
            <Alert color="danger" className="mb-5">
              {error}
            </Alert>
          )}

          {usNumbersOnly ? (
            <>
              <FormInputPhone
                id="telephone"
                label="Phone Number"
                value={telephone}
                onChange={setTelephone}
                placeholder="(___) ___-____"
              />

              <p className="text-sm text-slate-600 -mt-3 mb-6">
                US and Canadian numbers only.
              </p>
            </>
          ) : (
            <FormInput
              id="telephone"
              type="tel"
              label="Phone Number"
              value={telephone}
              onChange={(event) => setTelephone(event.target.value)}
              autoComplete="tel"
            />
          )}

          {/*
            The texting half of the page, under its own heading the way the email
            tab puts its lists under Frequency Preferences. It is a section rather
            than the page because the number above it is worth keeping whether or
            not we ever text it — and on a storefront that runs neither programme
            there is nothing here to head, so the heading goes with the boxes.
          */}
          {(offersMarketing || offersTransactional) && (
            <>
              <h2 className="font-bold text-lg md:text-xl mt-8 mb-2">SMS Preferences</h2>

              {/*
                The consent language a carrier reads. Our 10DLC campaign is
                registered off a screenshot of this block, and a carrier rejects
                consent text that is missing the brand name, the message
                frequency, the HELP/STOP instructions or the privacy and terms
                links — so all four are here rather than left to the checkbox
                descriptions beneath.

                It says "by checking a box below" because the boxes are what
                consent is given by; nothing about arriving on this page is
                agreement to anything, and both boxes are drawn unticked for a
                customer on neither list.

                The brand and both links come off the storefront being rendered,
                not out of this file. Nothing in shared/ names a brand: this
                component is mounted by whichever site holds a list, and a
                consent notice that named DiscountMags on MagSpot would be
                telling the customer they had agreed with somebody else. The
                links go through buildUrl for the same reason the rest of the
                page's do — AU serves from /au/, where a bare /terms is a 404.

                Each document is linked as the phrase that names it rather than
                as its own bare url: a customer reads a sentence, and a carrier
                shown the screenshot still has both links in front of them.
              */}
              <p className="text-sm text-slate-600 mb-4">
                By checking a box below, you agree to receive automated text messages from{' '}
                {siteName} at the number above. Consent is not a condition of purchase. Message
                frequency varies. Msg &amp; data rates may apply. Reply HELP for help or STOP to opt
                out. View{' '}
                <a
                  href={buildUrl('/terms')}
                  target="_blank"
                  rel="noreferrer"
                  className="underline font-medium"
                >
                  Terms of Use
                </a>{' '}
                and{' '}
                <a
                  href={buildUrl('/privacy')}
                  target="_blank"
                  rel="noreferrer"
                  className="underline font-medium"
                >
                  Privacy Policy
                </a>
              </p>

              {offersMarketing && (
                <FormCheckbox
                  id="marketing"
                  label={LIST_LABELS.marketing}
                  description="Recurring automated marketing texts about sales, new titles and personalized offers."
                  checked={marketing}
                  onChange={(event) => setMarketing(event.target.checked)}
                />
              )}

              {offersTransactional && (
                <FormCheckbox
                  id="transactional"
                  label={LIST_LABELS.transactional}
                  description="Text me about my orders, such as before a recurring subscription charge is processed."
                  checked={transactional}
                  onChange={(event) => setTransactional(event.target.checked)}
                />
              )}
            </>
          )}

          {/*
            The email tab's shape, minus its footnote. That footnote promises the
            transactional mail carries on regardless, and there is no equivalent
            promise to make here: this link takes the number off every list we
            run, order and subscription updates included, so we stop texting at
            all.

            Still conditional, which the email one is not. That page always has a
            list to leave; this one shows the link only to somebody actually on
            one, so a storefront running no programme — and a customer on none of
            them — is not offered a way out of nothing.
          */}
          {hasNumber && isSubscribedToAnything && (
            <>
              <h2 className="font-bold text-lg md:text-xl mt-8 mb-4">Unsubscribe</h2>

              <p className="text-slate-600 mb-2">
                To unsubscribe from all texts,{' '}
                <button
                  type="button"
                  onClick={unsubscribeAll}
                  disabled={isSaving}
                  className="bg-transparent border-0 p-0 underline text-slate-600 hover:text-slate-900 cursor-pointer disabled:cursor-default"
                >
                  click here
                </button>
                .
              </p>
            </>
          )}

          <div className="mt-8">
            <Button type="submit" color="primary" isLoading={isSaving} loadingText="Saving">
              Update
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}

SmsPreferences.layoutName = 'Layouts.Account';
